
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { FileIcon, Download, Calendar, HardDrive } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { formatBytes } from '@/utils/formatting';

interface FileBrowserProps {
  onFileSelect: (bucket: string, filePath: string, fileName: string) => void;
}

export const FileBrowser: React.FC<FileBrowserProps> = ({ onFileSelect }) => {
  const [selectedBucket, setSelectedBucket] = useState<string>('csv_uploads');
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingFile, setProcessingFile] = useState<string | null>(null);
  
  const bucketOptions = [
    { value: 'csv_uploads', label: 'CSV Uploads' },
    { value: 'raw-data-files', label: 'Raw Data Files' },
    { value: 'fund_documents', label: 'Fund Documents' }
  ];

  const loadFiles = async (bucket: string) => {
    setLoading(true);
    try {
      console.log('[FILE-BROWSER] Loading files from bucket:', bucket);
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .list('', {
          limit: 100,
          offset: 0,
          sortBy: { column: 'updated_at', order: 'desc' }
        });

      if (error) {
        console.error('[FILE-BROWSER] Error loading files:', error);
        throw error;
      }

      console.log('[FILE-BROWSER] Files loaded:', data?.length);

      const csvFiles = data?.filter(file => 
        file.name.toLowerCase().endsWith('.csv') ||
        file.metadata?.mimetype === 'text/csv'
      ) || [];

      setFiles(csvFiles);
      
      if (csvFiles.length === 0) {
        console.log('[FILE-BROWSER] No CSV files found in bucket:', bucket);
      }
      
    } catch (error: any) {
      console.error('[FILE-BROWSER] Error loading files:', error);
      toast({
        title: "Error loading files",
        description: `Failed to load files from ${bucket}: ${error.message}`,
        variant: "destructive",
      });
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles(selectedBucket);
  }, [selectedBucket]);

  const handleFileSelect = async (file: any) => {
    console.log('[FILE-BROWSER] File selected:', { file, bucket: selectedBucket });
    
    setProcessingFile(file.name);
    
    const fileSize = file.metadata?.size || 0;
    const isLargeFile = fileSize > 10 * 1024 * 1024; // 10MB threshold
    
    toast({
      title: isLargeFile ? "Processing large file..." : "Processing file...",
      description: `Analyzing ${file.name} (${formatBytes(fileSize)}). ${isLargeFile ? 'This may take a few minutes.' : 'Please wait...'}`,
    });
    
    try {
      await onFileSelect(selectedBucket, file.name, file.name);
    } finally {
      setProcessingFile(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
          <HardDrive className="w-5 h-5" />
          Select from Storage
        </h3>
      </div>
      
      <div>
        <label className="text-sm font-medium mb-2 block">Storage Bucket</label>
        <Select value={selectedBucket} onValueChange={setSelectedBucket} disabled={loading || Boolean(processingFile)}>
          <SelectTrigger>
            <SelectValue placeholder="Select bucket" />
          </SelectTrigger>
          <SelectContent>
            {bucketOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="text-center py-8">
            <Spinner size="lg" ariaLabel="Loading files" />
            <p className="text-sm text-gray-600 mt-2">Loading files...</p>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-8">
            <FileIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="text-sm text-gray-600">No CSV files found in this bucket</p>
            <p className="text-xs text-gray-500 mt-1">Try selecting a different bucket</p>
          </div>
        ) : (
          files.map((file) => {
            const isProcessing = processingFile === file.name;
            const fileSize = file.metadata?.size || 0;
            const isLargeFile = fileSize > 10 * 1024 * 1024;
            
            return (
              <div
                key={file.id}
                className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                  isProcessing ? 'bg-blue-50 border-blue-200' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <FileIcon className="w-4 h-4 text-gray-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{file.name}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Download className="w-3 h-3" />
                        {formatBytes(fileSize)}
                        {isLargeFile && <span className="text-orange-600">(Large file)</span>}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(file.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleFileSelect(file)}
                  disabled={isProcessing || Boolean(processingFile)}
                  className="ml-2"
                >
                  {isProcessing ? (
                    <>
                      <Spinner size="sm" className="mr-2" ariaLabel="Processing" />
                      Processing...
                    </>
                  ) : (
                    'Select'
                  )}
                </Button>
              </div>
            );
          })
        )}
      </div>

      <Button
        variant="outline"
        onClick={() => loadFiles(selectedBucket)}
        className="w-full"
        disabled={loading || Boolean(processingFile)}
      >
        {loading ? (
          <>
            <Spinner size="sm" className="mr-2" ariaLabel="Loading" />
            Loading...
          </>
        ) : (
          'Refresh Files'
        )}
      </Button>
    </div>
  );
};
